import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { WHATSAPP_LINK, money } from "@/config";
import { Separator } from "@/components/ui/separator";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [result, setResult] = useState(null);
  const [tries, setTries] = useState(0);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId || tries > 15) return;
    const t = setTimeout(() => {
      api.get(`/payments/status/${sessionId}`)
        .then(({ data }) => {
          setResult(data);
          if (data.payment_status !== "paid") setTries((n) => n + 1);
        })
        .catch(() => setTries((n) => n + 1));
    }, 2000);
    return () => clearTimeout(t);
  }, [sessionId, tries]);

  useEffect(() => {
    if (result?.payment_status === "paid" && !cleared.current) {
      cleared.current = true;
      clear();
    }
  }, [result, clear]);

  const paid = result?.payment_status === "paid";

  return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center sm:px-8" data-testid="payment-success-page">
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 14 }}>
        <CheckCircle2 size={72} className={`mx-auto ${paid ? "text-leaf" : "text-corn"}`} />
      </motion.div>
      {paid ? (
        <>
          <h1 className="mt-6 font-script text-4xl font-semibold text-cocoa">¡Gracias por tu pedido!</h1>
          <p className="mt-2 font-script font-semibold text-3xl text-orange">Ya se siente el sazón</p>
          <p className="mt-4 text-clay">
            Tu pedido <strong className="text-cocoa" data-testid="confirmed-order-number">{result.order_number}</strong> fue
            recibido y ya está en nuestra cocina. Te avisaremos por correo y WhatsApp cada avance.
          </p>
          {result.order && (
            <div className="mt-6 rounded-2xl border border-sand bg-white p-6 text-left" data-testid="confirmed-order-summary">
              <ul className="space-y-2">
                {result.order.items.map((i) => (
                  <li key={`${i.slug}-${i.variant_id}`} className="flex justify-between gap-4 text-sm">
                    <span className="text-cocoa">
                      {i.qty} × {i.name} <span className="text-clay">({i.variant_label})</span>
                    </span>
                    <span className="font-bold text-cocoa">{money(i.price * i.qty)}</span>
                  </li>
                ))}
              </ul>
              <Separator className="my-3 bg-sand" />
              <div className="flex justify-between text-sm text-clay">
                <span>Envío</span>
                <span>
                  {result.order.shipping > 0 ? money(result.order.shipping) : "Pick up (gratis)"}
                </span>
              </div>
              <div className="mt-1 flex justify-between font-display text-lg font-bold text-cocoa">
                <span>Total</span>
                <span data-testid="confirmed-order-total">{money(result.order.total)}</span>
              </div>
            </div>
          )}
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button asChild className="rounded-full bg-habanero px-8 py-6 font-bold text-white hover:bg-habanero-dark" data-testid="track-order-button">
              <Link to="/cuenta">Rastrear mi pedido</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-sand px-8 py-6 font-bold text-cocoa" data-testid="success-shop-button">
              <Link to="/tienda">Seguir comprando</Link>
            </Button>
          </div>
        </>
      ) : (
        <>
          <h1 className="mt-6 font-script text-3xl font-semibold text-cocoa">Confirmando tu pago...</h1>
          <p className="mt-4 text-clay" data-testid="payment-confirming">
            Estamos verificando tu pago con Stripe. Esto tarda unos segundos.
          </p>
          {tries > 15 && (
            <p className="mt-6 text-sm text-clay">
              Si tu pago fue descontado y no ves la confirmación, escríbenos por{" "}
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="font-bold text-habanero underline">WhatsApp</a>.
            </p>
          )}
        </>
      )}
    </div>
  );
}
